import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  User,
  Building2,
  ArrowLeft,
  ArrowRight,
  Loader2,
  UserPlus,
  Plus,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { contactsApi } from "../../lib/contactsApi";
import { formatApiErrorDetail } from "../../lib/api";
import { useAuthStore } from "../../store/authStore";

const emptyPersonForm = () => ({
  kind: "person",
  first_name: "",
  last_name: "",
  title: "",
  email: "",
  phone: "",
  status_id: "",
  company_id: "",
  tags: [],
  notes: "",
});

const emptyCompanyForm = () => ({
  kind: "company",
  name: "",
  website: "",
  industry: "",
  email: "",
  phone: "",
  address: "",
  tax_id: "",
  status_id: "",
  tags: [],
  notes: "",
});

const personDisplayName = (p) =>
  [p?.first_name, p?.last_name].filter(Boolean).join(" ") || p?.email || "—";

// Format a raw string into "+90 5XX XXX XX XX". Strips all non-digits, drops
// leading country codes (90 / 0) if present, keeps only the last 10 digits.
// Returns an empty string when there are no digits — lets the placeholder
// stay visible instead of showing a lonely "+90 ".
const formatTrPhone = (raw) => {
  if (!raw) return "";
  let digits = String(raw).replace(/\D/g, "");
  if (digits.startsWith("90")) digits = digits.slice(2);
  if (digits.startsWith("0")) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (!digits) return "";
  const parts = [];
  if (digits.length > 0) parts.push(digits.slice(0, 3));
  if (digits.length > 3) parts.push(digits.slice(3, 6));
  if (digits.length > 6) parts.push(digits.slice(6, 8));
  if (digits.length > 8) parts.push(digits.slice(8, 10));
  return `+90 ${parts.join(" ")}`;
};

/**
 * Yetkili kişi ekleyici — matches the "Bağlı şirket" picker style used on
 * the person form: single-select dropdown of unselected people + a side
 * button that spawns the "+ Yeni Kişi Ekle" nested dialog. Each pick lands
 * as a removable chip above the row so the composer supports multiple
 * authorized persons per company.
 */
function AuthorizedPeoplePicker({
  people,
  selectedIds,
  onAdd,
  onRemove,
  onRequestAddPerson,
}) {
  const available = useMemo(
    () => people.filter((p) => !selectedIds.includes(p.id)),
    [people, selectedIds],
  );
  const selectedDocs = useMemo(
    () =>
      selectedIds
        .map((id) => people.find((p) => p.id === id))
        .filter(Boolean),
    [people, selectedIds],
  );

  return (
    <div>
      {selectedDocs.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {selectedDocs.map((p) => (
            <span
              key={p.id}
              data-testid={`auth-chip-${p.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-800"
            >
              {personDisplayName(p)}
              <button
                type="button"
                onClick={() => onRemove(p.id)}
                className="hover:text-red-600"
                aria-label="Yetkiliyi kaldır"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-stretch gap-2">
        <div className="flex-1">
          <Select
            value="__placeholder__"
            onValueChange={(v) => {
              if (v && v !== "__placeholder__") onAdd(v);
            }}
          >
            <SelectTrigger data-testid="picker-person-select">
              <SelectValue placeholder="— Kişi seç —" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__placeholder__" disabled>
                — Kişi seç —
              </SelectItem>
              {available.map((p) => (
                <SelectItem
                  key={p.id}
                  value={p.id}
                  data-testid={`pick-person-${p.id}`}
                >
                  {personDisplayName(p)}
                  {p.email ? ` · ${p.email}` : ""}
                </SelectItem>
              ))}
              {available.length === 0 && (
                <SelectItem value="__empty__" disabled>
                  Eklenebilecek kişi kalmadı
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={onRequestAddPerson}
          data-testid="picker-add-person"
          className="whitespace-nowrap border-zinc-300 text-xs"
          title="Yeni kişi ekle"
        >
          <UserPlus className="mr-1 h-3.5 w-3.5" />
          + Yeni Kişi
        </Button>
      </div>
    </div>
  );
}

/**
 * Two-step wizard: pick kind → fill form. When `initialKind` is provided
 * (from the "Add person" / "Add company" list-page buttons), the wizard
 * jumps straight to step 2. Supports nested "quick-add opposite kind"
 * flow (add a person while creating a company and vice-versa).
 */
export default function AddContactDialog({
  open,
  onOpenChange,
  initialKind = null,
  companies = [],
  people = [],
  onCreated,
  onRequestAddOpposite,
  nestedCreated,
  isNested = false,
  defaultCompanyId = null,
}) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = ["super_admin", "ultra_admin"].includes(user?.role);
  const [step, setStep] = useState(initialKind ? 2 : 1);
  const [kind, setKind] = useState(initialKind || "person");
  const [form, setForm] = useState(
    initialKind === "company" ? emptyCompanyForm() : emptyPersonForm(),
  );
  const [statuses, setStatuses] = useState([]);
  const [tagDraft, setTagDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Super-admin quick "+ Statü ekle" popover state. Auto-selects the newly
  // created status on the parent form after save.
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [newStatusName, setNewStatusName] = useState("");
  const [newStatusColor, setNewStatusColor] = useState("#8b5cf6");
  const [savingStatus, setSavingStatus] = useState(false);

  // Yetkili kişiler (only relevant when kind === "company"). Persist as ids
  // during editing, then wire them to the company after save via PATCH.
  const [authorizedIds, setAuthorizedIds] = useState([]);

  // Nested "quick-add" dialog is now owned by the *parent* (ContactsListPage).
  // We just signal upward via `onRequestAddOpposite(kind)`. Avoids the file
  // rendering another instance of itself, which was tripping Babel's traversal.

  useEffect(() => {
    // Reset each time the dialog is (re)opened.
    if (open) {
      const k = initialKind || "person";
      setKind(k);
      setStep(initialKind ? 2 : 1);
      const initialForm =
        k === "company" ? emptyCompanyForm() : emptyPersonForm();
      // Preselect the linked company when the caller passes `defaultCompanyId`
      // (used by the company detail drawer's "+ Yeni Kişi" shortcut so the new
      // person is wired to the current company on the first save).
      if (k === "person" && defaultCompanyId) {
        initialForm.company_id = defaultCompanyId;
      }
      setForm(initialForm);
      setTagDraft("");
      setAuthorizedIds([]);
    }
  }, [open, initialKind, defaultCompanyId]);

  useEffect(() => {
    if (!open) return;
    // Prefer initialKind over the possibly-stale `kind` state — otherwise the
    // nested dialog (which mounts once and only toggles `open`) races and
    // fetches statuses for the outer form's kind, causing "Geçersiz statü"
    // on save.
    const k = initialKind || kind;
    contactsApi.listStatuses(k).then(setStatuses).catch(() => setStatuses([]));
  }, [open, initialKind, kind]);

  // React to nested creations bubbled from parent. Every value of
  // `nestedCreated` is a fresh contact doc + we consume it once.
  useEffect(() => {
    if (!nestedCreated) return;
    if (nestedCreated.kind === "person") {
      setAuthorizedIds((cur) =>
        cur.includes(nestedCreated.id) ? cur : [...cur, nestedCreated.id],
      );
    } else if (nestedCreated.kind === "company") {
      setForm((f) => ({ ...f, company_id: nestedCreated.id }));
    }
  }, [nestedCreated]);

  const pickKind = (k) => {
    setKind(k);
    setForm(k === "company" ? emptyCompanyForm() : emptyPersonForm());
    setAuthorizedIds([]);
    setStep(2);
  };

  const addTag = () => {
    const val = tagDraft.trim();
    if (!val) return;
    if (!form.tags.includes(val)) {
      setForm((f) => ({ ...f, tags: [...f.tags, val] }));
    }
    setTagDraft("");
  };
  const removeTag = (tag) =>
    setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== tag) }));

  const toggleAuth = (id) =>
    setAuthorizedIds((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    );

  const openNested = (k) => {
    // Delegates to the parent — the parent owns a second AddContactDialog
    // instance for the opposite kind so we don't self-recurse.
    onRequestAddOpposite?.(k);
  };

  const submitNewStatus = async () => {
    const name = newStatusName.trim();
    if (!name) return;
    setSavingStatus(true);
    try {
      const created = await contactsApi.createStatus({
        kind,
        name,
        color: newStatusColor,
        // Push to the tail — platform admin can reorder later.
        order: statuses.length,
        active: true,
      });
      setStatuses((prev) => [...prev, created]);
      setForm((f) => ({ ...f, status_id: created.id })); // auto-select
      toast.success(t("contacts.status_created_toast"));
      setStatusModalOpen(false);
      setNewStatusName("");
      setNewStatusColor("#8b5cf6");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    } finally {
      setSavingStatus(false);
    }
  };

  // The parent invokes this via ref-like escape hatch to bring newly-created
  // opposite-kind contacts back into this form. Exposed as `onNestedCreated`
  // through props (see below).

  const submit = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      payload.kind = kind;
      const created = await contactsApi.create(payload);
      toast.success(t("contacts.created_toast"));
      onCreated?.(created);

      // If we just created a company and the user linked authorized persons,
      // patch each person to point their company_id at the new company.
      if (kind === "company" && authorizedIds.length > 0) {
        await Promise.all(
          authorizedIds.map((pid) =>
            contactsApi
              .update(pid, { company_id: created.id })
              .then((updated) => {
                onCreated?.(updated); // treat as an update-through-create so list refreshes
              })
              .catch(() => {
                /* one failed link shouldn't undo the whole save */
              }),
          ),
        );
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl" data-testid="add-contact-dialog">
          <DialogHeader>
            <DialogTitle>
              {step === 1
                ? t("contacts.wizard_pick_kind_title")
                : kind === "person"
                  ? t("contacts.add_person")
                  : t("contacts.add_company")}
            </DialogTitle>
            {step === 1 && (
              <DialogDescription>
                {t("contacts.wizard_pick_kind_desc")}
              </DialogDescription>
            )}
          </DialogHeader>

          {step === 1 && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => pickKind("person")}
                data-testid="pick-kind-person"
                className="group flex flex-col items-start gap-2 rounded-xl border border-zinc-200 bg-white p-4 text-left transition-all hover:border-violet-400 hover:shadow-md"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow">
                  <User className="h-5 w-5" />
                </div>
                <div className="mt-2 text-sm font-semibold text-zinc-900">
                  {t("contacts.kind_person")}
                </div>
                <div className="text-xs text-zinc-500">
                  {t("contacts.kind_person_desc")}
                </div>
              </button>
              <button
                type="button"
                onClick={() => pickKind("company")}
                data-testid="pick-kind-company"
                className="group flex flex-col items-start gap-2 rounded-xl border border-zinc-200 bg-white p-4 text-left transition-all hover:border-sky-400 hover:shadow-md"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 text-white shadow">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="mt-2 text-sm font-semibold text-zinc-900">
                  {t("contacts.kind_company")}
                </div>
                <div className="text-xs text-zinc-500">
                  {t("contacts.kind_company_desc")}
                </div>
              </button>
            </div>
          )}

          {step === 2 && (
            <form onSubmit={submit} className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
              {kind === "person" ? (
                <>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <Label>{t("contacts.field_first_name")}</Label>
                      <Input
                        value={form.first_name}
                        onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                        data-testid="person-first-name"
                      />
                    </div>
                    <div>
                      <Label>{t("contacts.field_last_name")}</Label>
                      <Input
                        value={form.last_name}
                        onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                        data-testid="person-last-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>{t("contacts.field_title")}</Label>
                    <Input
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      placeholder="CEO, Yazılım Mühendisi…"
                    />
                  </div>
                  {/* Company select with inline "+ Yeni şirket" escape hatch */}
                  <div>
                    <Label>{t("contacts.field_company")}</Label>
                    <div className="flex items-stretch gap-2">
                      <div className="flex-1">
                        <Select
                          value={form.company_id || "__none__"}
                          onValueChange={(v) =>
                            setForm({
                              ...form,
                              company_id: v === "__none__" ? "" : v,
                            })
                          }
                        >
                          <SelectTrigger data-testid="person-company-select">
                            <SelectValue placeholder={t("contacts.field_company_none")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">
                              {t("contacts.field_company_none")}
                            </SelectItem>
                            {companies.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => openNested("company")}
                        data-testid="add-company-inline-btn"
                        className="whitespace-nowrap border-zinc-300 text-xs"
                        title="Yeni şirket ekle"
                      >
                        <Building2 className="mr-1 h-3.5 w-3.5" />
                        + Yeni Şirket
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label>{t("contacts.field_company_name")}</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="company-name"
                      autoFocus
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <Label>{t("contacts.field_website")}</Label>
                      <Input
                        value={form.website}
                        onChange={(e) => setForm({ ...form, website: e.target.value })}
                        placeholder="acme.com"
                      />
                    </div>
                    <div>
                      <Label>{t("contacts.field_industry")}</Label>
                      <Input
                        value={form.industry}
                        onChange={(e) => setForm({ ...form, industry: e.target.value })}
                        placeholder="Tech, İnşaat…"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>{t("contacts.field_address")}</Label>
                    <Input
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>{t("contacts.field_tax_id")}</Label>
                    <Input
                      value={form.tax_id}
                      onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                    />
                  </div>
                </>
              )}

              {/* Shared block */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label>{t("contacts.field_email")}</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="ornek@sirket.com"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Label>{t("contacts.field_phone")}</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: formatTrPhone(e.target.value) })
                    }
                    onKeyDown={(e) => {
                      // Allow control keys and digits only; block letters and
                      // most special characters at the source so users can't
                      // paste garbage character-by-character either.
                      const allowed = [
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                        "Home",
                        "End",
                      ];
                      if (allowed.includes(e.key)) return;
                      if (e.metaKey || e.ctrlKey) return; // copy/paste
                      if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                    }}
                    inputMode="numeric"
                    placeholder="+90 532 333 33 33"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label>{t("contacts.field_status")}</Label>
                <div className="flex items-stretch gap-2">
                  <div className="flex-1">
                    <Select
                      key={`status-select-${statuses.length}`}
                      value={form.status_id || "__none__"}
                      onValueChange={(v) =>
                        setForm({ ...form, status_id: v === "__none__" ? "" : v })
                      }
                    >
                      <SelectTrigger data-testid="contact-status-select">
                        <SelectValue placeholder={t("contacts.select_status")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t("contacts.select_status")}</SelectItem>
                        {statuses
                          .filter((s) => s.active !== false)
                          .map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {isSuperAdmin && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStatusModalOpen(true)}
                      data-testid="add-status-inline-btn"
                      className="whitespace-nowrap border-zinc-300 text-xs"
                      title={t("contacts.add_status")}
                    >
                      <Plus className="mr-1 h-3.5 w-3.5" />
                      {t("contacts.add_status")}
                    </Button>
                  )}
                </div>
              </div>

              {/* Yetkili Kişiler — only for company form. Layout matches the
                  person form's "Bağlı Şirket" picker: single-select dropdown
                  + side "+ Yeni Kişi" button, with chosen people rendered as
                  removable chips above.
                  Hidden when this dialog is spawned in nested mode (i.e. the
                  user is mid-way through creating a Person and just wants to
                  quickly add a Company to link to) — in that flow the parent
                  is *already* the authorized person, so surfacing another
                  people picker inside the nested modal is confusing. */}
              {kind === "company" && !isNested && (
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <Label>Yetkili Kişiler ({authorizedIds.length})</Label>
                    {authorizedIds.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setAuthorizedIds([])}
                        className="text-[11px] text-zinc-500 hover:text-zinc-900"
                      >
                        Temizle
                      </button>
                    )}
                  </div>
                  <AuthorizedPeoplePicker
                    people={people}
                    selectedIds={authorizedIds}
                    onAdd={(id) =>
                      setAuthorizedIds((cur) =>
                        cur.includes(id) ? cur : [...cur, id],
                      )
                    }
                    onRemove={toggleAuth}
                    onRequestAddPerson={() => openNested("person")}
                  />
                </div>
              )}

              <div>
                <Label>{t("contacts.field_tags")}</Label>
                <Input
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addTag();
                    } else if (
                      e.key === "Backspace" &&
                      !tagDraft &&
                      form.tags.length > 0
                    ) {
                      // Convenient: hitting Backspace on an empty draft pops
                      // the most-recently added chip. Matches Gmail's chip UX.
                      e.preventDefault();
                      removeTag(form.tags[form.tags.length - 1]);
                    }
                  }}
                  placeholder={t("contacts.field_tags_placeholder")}
                  data-testid="contact-tag-input"
                />
                {form.tags.length > 0 && (
                  <div
                    className="mt-2 flex flex-wrap gap-1.5"
                    data-testid="contact-tag-list"
                  >
                    {form.tags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => removeTag(tag)}
                        data-testid={`contact-tag-${tag}`}
                        className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2.5 py-0.5 text-[11px] font-medium text-violet-800 transition hover:bg-red-100 hover:text-red-700"
                      >
                        {tag}
                        <X className="h-3 w-3" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label>{t("contacts.field_notes")}</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                />
              </div>

              <DialogFooter className="flex-row items-center justify-between gap-2">
                {!initialKind ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setStep(1)}
                    className="gap-1"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t("contacts.back")}
                  </Button>
                ) : (
                  <span />
                )}
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => onOpenChange(false)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="save-contact-btn"
                    className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowRight className="h-4 w-4" />
                    )}
                    {t("contacts.save")}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Super-admin only inline "Yeni Statü Ekle" mini modal. Rendered as a
          sibling Dialog so it stacks on top of the main add-contact dialog
          via Radix's own portal — no z-index gymnastics needed. */}
      <Dialog open={statusModalOpen} onOpenChange={setStatusModalOpen}>
        <DialogContent className="max-w-sm" data-testid="add-status-dialog">
          <DialogHeader>
            <DialogTitle>{t("contacts.add_status_title")}</DialogTitle>
            <DialogDescription>
              {kind === "company"
                ? t("contacts.add_status_desc_company")
                : t("contacts.add_status_desc_person")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("contacts.status_name")}</Label>
              <Input
                autoFocus
                value={newStatusName}
                onChange={(e) => setNewStatusName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submitNewStatus();
                  }
                }}
                placeholder={
                  kind === "company" ? "Bayi, Distribütör…" : "VIP, Aday…"
                }
                data-testid="new-status-name"
                maxLength={40}
              />
            </div>
            <div>
              <Label>{t("contacts.status_color")}</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  "#059669",
                  "#f59e0b",
                  "#8b5cf6",
                  "#ef4444",
                  "#0ea5e9",
                  "#ec4899",
                  "#14b8a6",
                  "#a3a3a3",
                ].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setNewStatusColor(c)}
                    aria-label={`Renk ${c}`}
                    className={`h-7 w-7 rounded-full border-2 transition ${
                      newStatusColor === c
                        ? "border-zinc-900 scale-110"
                        : "border-transparent hover:scale-105"
                    }`}
                    style={{ backgroundColor: c }}
                    data-testid={`status-color-${c}`}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setStatusModalOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={submitNewStatus}
              disabled={savingStatus || !newStatusName.trim()}
              data-testid="save-new-status-btn"
              className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
            >
              {savingStatus ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {t("contacts.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
